import { Trans, useI18n } from '~/common/context';

import { notify, SoftBadge, Switch, useConfirmation } from '@wyz/ui';
import clsx from 'clsx';
import React from 'react';
import { useManageAutomakerGroups } from '~/common/api';
import { PrivateComponent } from '~/common/components';
import {
  CommonErrorCodes,
  CommonErrorCodesLookup,
  ROLES,
} from '~/common/helpers';
import { useAccessControl } from '~/common/hooks';
import { IAutomakerGroup } from '~/common/types';
import { AUTOMAKER_GROUPS_STATUSES } from '~/connect/schemas';

interface Props {
  automakerGroup: IAutomakerGroup;
}

const LABELS = {
  [AUTOMAKER_GROUPS_STATUSES.ENABLED]: 'app.common.status.enabled',
  [AUTOMAKER_GROUPS_STATUSES.DISABLED]: 'app.common.status.disabled',
} as const;
const VARIANT = {
  [AUTOMAKER_GROUPS_STATUSES.ENABLED]: 'success',
  [AUTOMAKER_GROUPS_STATUSES.DISABLED]: 'danger',
} as const;

export const AutomakerGroupStatusCell = ({ automakerGroup }: Props) => {
  const manageAutomakerGroups = useManageAutomakerGroups({
    patch: {
      enabled: true,
      onError: (error) => {
        if (error.message === CommonErrorCodes.ERR_CRITERIA_VALUES_IN_USE) {
          notify.error(
            <Trans
              i18nKey={CommonErrorCodesLookup[error.message]}
              values={{
                countCustomer: error.meta.CUSTOMER,
                countSupplierTemplate: error.meta.SUPPLIER_TEMPLATE,
                countBrandTemplate: error.meta.BRAND_TEMPLATE,
                countAdvert: error.meta.ADVERT,
                countTheme: error.meta.THEME,
                countSettingTemplate: error.meta.SETTING_TEMPLATE,
              }}
            />,
            { id: 'toast-error-automaker-groups-criteria-in-use-failed' },
          );
        }
      },
    },
  });
  const { t } = useI18n();
  const acl = useAccessControl();

  const ask = useConfirmation();

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const targetStatus = event.target.checked
      ? AUTOMAKER_GROUPS_STATUSES.ENABLED
      : AUTOMAKER_GROUPS_STATUSES.DISABLED;
    const confirm = () => {
      manageAutomakerGroups.patch.mutate({
        id: automakerGroup.id,
        body: {
          name: automakerGroup.name,
          status: targetStatus,
        },
      });
    };
    if (targetStatus === AUTOMAKER_GROUPS_STATUSES.DISABLED) {
      ask({
        title: t('app.common.modals.disable.title'),
        description: t('app.common.modals.disable.message'),
        cancelBtnText: t('app.common.button.cancel'),
        confirmBtnText: t('app.common.button.confirm'),
      }).onConfirm(confirm);
    } else {
      confirm();
    }
  };
  return (
    <>
      <div
        className={clsx('d-flex justify-content-center', {
          'show-on-hover': acl.isGranted([ROLES.ROLE_CUSTOMER_UPDATE_STATUS]),
        })}
      >
        <SoftBadge
          className='reverse-controlled fs-xxs'
          bg={VARIANT[automakerGroup.status]}
          pill
        >
          <Trans i18nKey={LABELS[automakerGroup.status]} />
        </SoftBadge>
        <PrivateComponent roles={[ROLES.ROLE_AUTOMAKER_UPDATE]}>
          <div className='controlled'>
            <Switch
              checked={
                automakerGroup.status === AUTOMAKER_GROUPS_STATUSES.ENABLED
              }
              onChange={onChange}
              id={`${automakerGroup.id}-toggle`}
              size='md'
            />
          </div>
        </PrivateComponent>
      </div>
    </>
  );
};
